import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useFieldArray } from 'react-hook-form';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LinkIcon, Loader2, Mail, MapPin, Phone, PlusIcon, TrashIcon } from 'lucide-react';
import { COMPANY_TYPES, NO_COMPANY_TYPE } from './_enums';
import { Loading } from '@/components/ui/loading';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Error } from '@/components/ui/error';
import { useSectorTypes } from '@/data/sectorType';
import { usePostCompany, useCompanies, useSeedProfiles } from '@/data/company';
import { mutateListOnPost } from '@/helpers/swr';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const formSchema = z.object({
  company_name: z.string().min(1).max(100),
  company_description: z.string().max(200).optional(),
  company_type: z.string(),
  website: z.string().optional(),
  address: z.string().max(200).optional(),
  phone: z.string().max(50).optional(),
  email: z.string().max(100).optional(),
  is_market_data: z.boolean(),
  is_market_data_visible: z.boolean(),
  is_analytics: z.boolean(),
  is_v2_estimate: z.boolean(),
  is_bid_management: z.boolean(),
  is_precon_management: z.boolean(),
  is_demo: z.boolean(),
  default_sector_type_uid: z.string().min(1),
  notes: z.string().optional(),
  ai_agent_runs_per_month: z.coerce.number().int().min(1),
  seed_profiles: z.array(z.string()),
  company_domains: z.array(z.object({ domain: z.string().regex(/^(?!:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/, 'Invalid domain') })).min(1, 'At least one domain is required')
});

const Add = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const navigate = useNavigate();
  const postCompany = usePostCompany();
  const companies = useCompanies();
  const sectorTypes = useSectorTypes();
  const seedProfiles = useSeedProfiles();
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      company_name: '',
      company_description: '',
      company_type: NO_COMPANY_TYPE,
      website: '',
      address: '',
      phone: '',
      email: '',
      is_market_data: true,
      is_market_data_visible: true,
      is_analytics: true,
      is_v2_estimate: true,
      is_bid_management: true,
      is_precon_management: true,
      is_demo: false,
      default_sector_type_uid: '',
      notes: '',
      ai_agent_runs_per_month: 0,
      seed_profiles: [],
      company_domains: [{ domain: '' }]
    }
  });
  const isDemo = form.watch('is_demo');

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'company_domains'
  });

  const onSubmit = async (values) => {
    try {
      setIsSubmitting(true);
      setApiError('');
      const payload = { ...values, company_type: values.company_type === NO_COMPANY_TYPE ? null : values.company_type };
      if (!payload.is_demo || !payload.seed_profiles.length) {
        delete payload.seed_profiles;
      }
      const newCompany = await postCompany(payload);
      await companies.mutate((currentCompanies) => mutateListOnPost(currentCompanies, newCompany), false);
      setIsSubmitting(false);
      navigate(`/company/${newCompany.uid}`);
    } catch (err) {
      setIsSubmitting(false);
      setApiError(err.message);
    }
  };

  const handleAddDomain = () => {
    append({ domain: '' });
  };

  const handleRemoveDomain = (index) => {
    remove(index);
  };

  return (
    <>
      <Breadcrumb className="mb-4">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/company">Companies</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Add</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <Card className="w-full">
        <CardHeader>
          <CardTitle>Add New Company</CardTitle>
          <CardDescription>Fill out the form to add a new company to Zebel.</CardDescription>
        </CardHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <CardContent className="grid gap-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="company_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company Name</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Enter company name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="company_description"
                  render={({ field }) => (
                    <FormItem className="col-span-2">
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea {...field} placeholder="Enter company description" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="company_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company Type</FormLabel>
                      <FormControl>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a company type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NO_COMPANY_TYPE}>No type</SelectItem>
                            {COMPANY_TYPES.map((companyType) => (
                              <SelectItem key={companyType} value={companyType}>
                                {companyType}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="address"
                  startIcon={<MapPin />}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Address</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Enter company address" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="website"
                  startIcon={<LinkIcon />}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Website</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Enter company website" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phone"
                  startIcon={<Phone />}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone Number</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Enter company phone number" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  startIcon={<Mail />}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input {...field} type="email" placeholder="Enter company email" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <Separator />
              <div>
                <h3 className="font-medium text-lg">Company Notes</h3>
                <p className="text-muted-foreground text-sm">Save notes about this company for internal use</p>
              </div>
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes</FormLabel>
                    <FormControl>
                      <Textarea {...field} className="h-[150px]" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Separator />
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-lg">Market Data</h3>
                  <p className="text-muted-foreground text-sm">Configure market data preferences for this company</p>
                </div>

                <FormField
                  control={form.control}
                  name="is_market_data"
                  render={({ field }) => (
                    <FormItem className="flex items-center gap-2 space-y-0">
                      <FormControl className="flex items-end">
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <FormLabel>Include company data in aggregated market data</FormLabel>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="is_market_data_visible"
                  render={({ field }) => (
                    <FormItem className="flex items-center gap-2 space-y-0">
                      <FormControl className="flex items-end">
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <FormLabel>Allow this company to view market data</FormLabel>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="default_sector_type_uid"
                  render={({ field }) => (
                    <FormItem className="">
                      <FormLabel>Default Market Data Sector Type</FormLabel>
                      <FormControl>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a sector type" />
                          </SelectTrigger>
                          <SelectContent>
                            {sectorTypes.data.query.map((sectorTypeUid) => (
                              <SelectItem key={sectorTypeUid} value={sectorTypeUid}>
                                {sectorTypes.data.content[sectorTypeUid].label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <Separator />
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-lg">Visible Features</h3>
                  <p className="text-muted-foreground text-sm">Configure what features this company can view within Zebel</p>
                </div>
                <FormField
                  control={form.control}
                  name="is_analytics"
                  render={({ field }) => (
                    <FormItem className="flex items-center gap-2 space-y-0">
                      <FormControl className="flex items-end">
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <FormLabel>Allow this company to view Analytics</FormLabel>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="is_v2_estimate"
                  render={({ field }) => (
                    <FormItem className="flex items-center gap-2 space-y-0">
                      <FormControl className="flex items-end">
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <FormLabel>Allow this company to view V2 Estimate Page</FormLabel>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="is_bid_management"
                  render={({ field }) => (
                    <FormItem className="flex items-center gap-2 space-y-0">
                      <FormControl className="flex items-end">
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <FormLabel>Allow this company to view bid management</FormLabel>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="is_precon_management"
                  render={({ field }) => (
                    <FormItem className="flex items-center gap-2 space-y-0">
                      <FormControl className="flex items-end">
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <FormLabel>Allow this company to view precon management</FormLabel>
                    </FormItem>
                  )}
                />
              </div>
              <Separator />
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-lg">Usage Limits</h3>
                  <p className="text-muted-foreground text-sm">Configure usage limits for this company</p>
                </div>
                <FormField
                  control={form.control}
                  name="ai_agent_runs_per_month"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>AI Agent Runs Per Month</FormLabel>
                      <FormControl>
                        <Input {...field} type="number" min={0} placeholder="0" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <Separator />
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-lg">Demo Company</h3>
                  <p className="text-muted-foreground text-sm">Mark this company as a demo and optionally seed it with template data</p>
                </div>
                <FormField
                  control={form.control}
                  name="is_demo"
                  render={({ field }) => (
                    <FormItem className="flex items-center gap-2 space-y-0">
                      <FormControl className="flex items-end">
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={(checked) => {
                            field.onChange(checked);
                            if (!checked) {
                              form.setValue('seed_profiles', []);
                            }
                          }}
                        />
                      </FormControl>
                      <FormLabel>This is a demo company</FormLabel>
                    </FormItem>
                  )}
                />
                {isDemo && seedProfiles.data && (
                  <div className="space-y-3 ml-6">
                    <p className="text-sm font-medium">Seed Profiles</p>
                    <p className="text-muted-foreground text-sm">
                      Select which seed profiles to create for this company. This will create a template and some sample Projects + Historical Records for that sector.
                    </p>
                    {seedProfiles.data.query.map((profileKey) => {
                      const profile = seedProfiles.data.content[profileKey];
                      return (
                        <FormField
                          key={profileKey}
                          control={form.control}
                          name="seed_profiles"
                          render={({ field }) => (
                            <FormItem className="flex items-center gap-2 space-y-0">
                              <FormControl className="flex items-end">
                                <Checkbox
                                  checked={field.value?.includes(profileKey)}
                                  onCheckedChange={(checked) => {
                                    const current = field.value || [];
                                    field.onChange(checked ? [...current, profileKey] : current.filter((k) => k !== profileKey));
                                  }}
                                />
                              </FormControl>
                              <FormLabel>{profile.label}</FormLabel>
                            </FormItem>
                          )}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
              <Separator />
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-lg">Company Domains</h3>
                  <p className="text-muted-foreground text-sm">
                    Configure the domains that can be invited to this company. Most companies will have a single domain. Domains are unique to companies and cannot be changed after
                    creation.
                  </p>
                </div>
                {fields.map((field, index) => (
                  <FormField
                    key={field.id}
                    control={form.control}
                    name={`company_domains.${index}.domain`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Domain {index + 1}</FormLabel>
                        <FormControl>
                          <div className="flex items-center gap-2">
                            <Input {...field} placeholder="Enter domain" />
                            {fields.length > 1 && (
                              <Button type="button" size="icon" variant="outline" onClick={() => handleRemoveDomain(index)}>
                                <TrashIcon />
                              </Button>
                            )}
                          </div>
                        </FormControl>

                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ))}
                <Button type="button" size="sm" variant="outline" onClick={handleAddDomain}>
                  <PlusIcon />
                  Add Domain
                </Button>
              </div>
              {apiError && <Error message={apiError} />}
            </CardContent>
            <CardFooter className="flex justify-end">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="animate-spin" />}
                Add Company
              </Button>
            </CardFooter>
          </form>
        </Form>
      </Card>
    </>
  );
};

const Wrapper = () => {
  const sectorTypes = useSectorTypes();
  const seedProfiles = useSeedProfiles();

  if (sectorTypes.isLoading && !sectorTypes.data) {
    return <Loading />;
  }
  if (seedProfiles.isLoading && !seedProfiles.data) {
    return <Loading />;
  }

  if (sectorTypes.error) {
    return <Error message="An error occurred loading sector types" />;
  }
  if (seedProfiles.error) {
    return <Error message="An error occurred loading seed profiles" />;
  }

  return <Add />;
};

export default Wrapper;
